import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n";
import JuhuuIcon from "../newHomeRoute/assets/juhuu-icon.png";

const PARTNER_LOGOS = [
  ["oebb", "ÖBB"],
  ["fellbachgo", "Fellbach.go!"],
  ["koch-partner", "Koch & Partner"],
  ["allgaeutissimo", "Allgäutissimo Bikeshop"],
  ["velodepo", "VELODEPO"],
  ["opg", "OPG"],
  ["greenguard", "Greenguard"],
  ["emz", "emz"],
  ["hochschule-ansbach", "Hochschule Ansbach"],
  ["gablitz", "Gablitz"],
  ["mauerbach", "Mauerbach"],
  ["purkersdorf", "Stadtgemeinde Purkersdorf"],
  ["w5ww", "Wir 5 im Wienerwald"],
];

const kicker = "text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu";
const h2Section =
  "text-[clamp(1.6rem,2.5vw,2.3rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink";
const mockShell =
  "overflow-hidden rounded-2xl border border-line-strong bg-white shadow-lg";
const trHead =
  "bg-bg-alt text-[.66rem] font-bold uppercase tracking-[.05em] text-muted";

const Check = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    className="mt-px h-5 w-5 flex-shrink-0 text-juhuu"
  >
    <path d="m5 12 5 5L20 7" />
  </svg>
);

const PinIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-[15px] w-[15px] flex-shrink-0 text-juhuu"
  >
    <path d="M12 21s-7-4.5-7-10a7 7 0 0 1 14 0c0 5.5-7 10-7 10Z" />
    <circle cx="12" cy="11" r="2.5" />
  </svg>
);

/** Browser chrome bar used by every mock. */
function MockBar({ url }: { url: string }) {
  return (
    <div className="flex items-center gap-3 border-b border-line bg-bg-alt px-[1.1rem] py-[.7rem]">
      <div className="flex gap-1.5">
        <span className="h-[11px] w-[11px] rounded-full bg-line-strong" />
        <span className="h-[11px] w-[11px] rounded-full bg-line-strong" />
        <span className="h-[11px] w-[11px] rounded-full bg-line-strong" />
      </div>
      <div className="flex h-7 flex-1 items-center rounded-full border border-line bg-white px-3.5 text-[.72rem] text-light">
        {url}
      </div>
    </div>
  );
}

const CAP_ICONS = [
  <>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M8 2v4M16 2v4M3 10h18" />
  </>,
  <>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
  </>,
  <>
    <path d="M12 21s-7-4.5-7-10a7 7 0 0 1 14 0c0 5.5-7 10-7 10Z" />
    <circle cx="12" cy="11" r="2.5" />
  </>,
  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />,
  <>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </>,
  <>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h6M16 15h2" />
  </>,
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  </>,
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </>,
];

const SIDE_ICONS: Record<string, JSX.Element> = {
  home: <path d="M3 11l9-8 9 8M5 9v11h14V9" />,
  boxes: <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />,
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18M8 2v4M16 2v4" />
    </>
  ),
  transactions: <path d="M2 7h20M2 7l3-3M22 17H2m20 0-3 3" />,
  map: <path d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3z" />,
  booking: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 2v4M16 2v4M3 10h18" />
    </>
  ),
  billing: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </>
  ),
  support: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
    </>
  ),
};

const TIMELINE_ROWS: {
  loc: string;
  bars: { l: number; w: number; c: string; t?: string }[];
}[] = [
  {
    loc: "Box 1",
    bars: [
      { l: 14, w: 18, c: "#6d16fa" },
      { l: 46, w: 26, c: "#e6dbff" },
    ],
  },
  { loc: "Box 2", bars: [{ l: 30, w: 30, c: "#6d16fa", t: "Daily" }] },
  {
    loc: "Box 3",
    bars: [
      { l: 8, w: 14, c: "#f5d57e" },
      { l: 40, w: 34, c: "#6d16fa", t: "2 Day" },
    ],
  },
  { loc: "Box 4", bars: [{ l: 58, w: 30, c: "#e6dbff" }] },
];

const DEVICES: [string, boolean, boolean][] = [
  ["SPS 01", true, false],
  ["Turm A Box 01", true, true],
  ["Turm A Box 02", true, true],
  ["Turm A Box 03", false, true],
  ["BikeBox 04", true, true],
];

const CUSTOMERS: [string, string, boolean][] = [
  ["Mara Weiß", "mara.weiss@gmail.com", true],
  ["Tobias Frank", "t.frank@outlook.de", true],
  ["Lena Hofer", "lena.hofer@web.de", true],
  ["Jonas Berger", "jberger@gmx.at", false],
  ["Sophie Kern", "sophie.kern@gmail.com", true],
  ["David Moser", "d.moser@web.de", true],
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function NewDashboardRoute() {
  const { t } = useTranslation();

  const list = (base: string, n: number) =>
    Array.from({ length: n }, (_, i) => t(`${base}.${i}`));

  const bullets = (base: string) => (
    <div className="flex flex-col gap-3">
      {list(base, 3).map((b, i) => (
        <div key={i} className="flex items-start gap-2.5">
          <Check />
          <span className="text-[.95rem] text-ink-2">{b}</span>
        </div>
      ))}
    </div>
  );

  const sideItem = (icon: JSX.Element, label: string, on = false) => (
    <span
      className={`mb-[.12rem] flex items-center gap-2.5 rounded-[9px] px-[.55rem] py-2 text-[.82rem] ${
        on
          ? "bg-juhuu-soft font-bold text-juhuu-deep"
          : "font-semibold text-muted"
      }`}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="h-[15px] w-[15px] flex-shrink-0"
      >
        {icon}
      </svg>
      {label}
    </span>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <main>
        {/* ── Hero ─────────────────────────────────────────── */}
        <section className="relative overflow-hidden px-4 md:px-8 pt-32 pb-[clamp(2rem,4vw,3rem)]">
          <div className="pointer-events-none absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-juhuu-glow blur-[90px]" />
          <div className="relative mx-auto max-w-[820px] text-center">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-juhuu-soft-2 bg-juhuu-soft px-3.5 py-1.5 text-sm font-bold text-juhuu-deep">
              <span className="h-[7px] w-[7px] rounded-full bg-juhuu" />
              {t("routes.dashboard.page.heroBadge")}
            </div>
            <h1 className="mt-5 text-[clamp(2.3rem,4.6vw,3.9rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-ink">
              {t("routes.dashboard.page.heroTitleLine1")}
              <br />
              {t("routes.dashboard.page.heroTitleLine2Prefix")}{" "}
              <span className="text-juhuu">
                {t("routes.dashboard.page.heroTitleHighlight")}
              </span>
              .
            </h1>
            <p className="mx-auto mt-5 max-w-[580px] text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
              {t("routes.dashboard.page.heroDescription")}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/meeting"
                className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
              >
                {t("routes.dashboard.page.ctaBookDemo")}
              </Link>
              <a
                href="https://identity.juhuu.app/auth"
                className="inline-flex items-center rounded-full border border-line-strong px-7 py-3.5 font-semibold text-ink transition-colors hover:border-juhuu hover:text-juhuu"
              >
                {t("routes.dashboard.page.ctaLogin")}
              </a>
            </div>
          </div>

          {/* Dashboard mock */}
          <div className="relative mx-auto mt-12 max-w-[1140px]">
            <div className={mockShell}>
              <MockBar url="dashboard.juhuu.app" />
              <div className="grid md:grid-cols-[208px_1fr]">
                {/* sidebar */}
                <div className="hidden border-r border-line bg-bg-alt p-4 md:block">
                  <div className="mb-3.5 flex items-center gap-2.5 border-b border-line px-1 pb-4">
                    <span className="grid h-[30px] w-[30px] place-items-center overflow-hidden rounded-lg bg-ink">
                      <img
                        src={JuhuuIcon}
                        alt="JUHUU"
                        className="h-5 w-5 object-contain"
                      />
                    </span>
                    <span className="text-[.86rem] font-bold text-ink">
                      JUHUU
                    </span>
                  </div>
                  {sideItem(
                    SIDE_ICONS.home,
                    t("routes.dashboard.page.navHome"),
                    true
                  )}
                  <div className="mx-[.3rem] mb-1.5 mt-3.5 text-[.64rem] font-bold uppercase tracking-[.1em] text-light">
                    {t("routes.dashboard.page.navQuickActions")}
                  </div>
                  {sideItem(
                    SIDE_ICONS.boxes,
                    t("routes.dashboard.page.navBikeBoxes")
                  )}
                  {sideItem(
                    SIDE_ICONS.calendar,
                    t("routes.dashboard.page.navCalendar")
                  )}
                  {sideItem(
                    SIDE_ICONS.transactions,
                    t("routes.dashboard.page.navTransactions")
                  )}
                  {sideItem(SIDE_ICONS.map, t("routes.dashboard.page.navMap"))}
                  <div className="mx-[.3rem] mb-1.5 mt-3.5 text-[.64rem] font-bold uppercase tracking-[.1em] text-light">
                    {t("routes.dashboard.page.navOperators")}
                  </div>
                  {sideItem(
                    SIDE_ICONS.booking,
                    t("routes.dashboard.page.navBooking")
                  )}
                  {sideItem(
                    SIDE_ICONS.billing,
                    t("routes.dashboard.page.navBilling")
                  )}
                  {sideItem(
                    SIDE_ICONS.support,
                    t("routes.dashboard.page.navSupport")
                  )}
                </div>

                {/* content */}
                <div className="min-w-0 bg-white px-[1.4rem] py-[1.3rem]">
                  <div className="text-[1.4rem] font-extrabold text-ink">
                    {t("routes.dashboard.page.mockTitle")}
                  </div>
                  <p className="mb-[1.1rem] text-[.84rem] text-muted">
                    {t("routes.dashboard.page.mockWelcome")}
                  </p>

                  <div className="mb-4 grid gap-[.7rem] sm:grid-cols-2 lg:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-line bg-white px-[.9rem] py-[.8rem]"
                      >
                        <div className="text-[.66rem] font-semibold uppercase leading-[1.2] tracking-[.06em] text-light">
                          {t(`routes.dashboard.page.kpis.${i}.label`)}
                        </div>
                        <div className="mt-[.45rem] text-[1.32rem] font-extrabold leading-none tracking-[-0.02em] text-ink tabular-nums">
                          {t(`routes.dashboard.page.kpis.${i}.value`)}
                        </div>
                        <div
                          className={`mt-[.35rem] text-[.7rem] font-bold tabular-nums ${
                            i < 2 ? "text-juhuu" : "text-light"
                          }`}
                        >
                          {t(`routes.dashboard.page.kpis.${i}.delta`)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="overflow-hidden rounded-xl border border-line shadow-sm">
                    <div className="flex items-center justify-between border-b border-line px-[1.1rem] py-[.85rem]">
                      <div className="flex items-center gap-2 text-[.9rem] font-bold text-ink">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="h-[17px] w-[17px] text-juhuu"
                        >
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 8v4M12 16h.01" />
                        </svg>
                        {t("routes.dashboard.page.incidentsTitle")}
                      </div>
                      <span className="rounded-[7px] border border-line-strong px-[.65rem] py-[.28rem] text-[.7rem] font-bold text-ink-2">
                        {t("routes.dashboard.page.incidentsOverview")}
                      </span>
                    </div>
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between gap-2.5 border-b border-line px-[1.1rem] py-[.7rem] text-[.8rem] last:border-b-0"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <PinIcon />
                          <span className="truncate text-ink-2">
                            {t(`routes.dashboard.page.incidents.${i}.name`)}
                          </span>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2">
                          <span className="whitespace-nowrap rounded-full bg-[#fdf3d4] px-[.55rem] py-[.2rem] text-[.66rem] font-bold text-[#8a6d12]">
                            {t("routes.dashboard.page.incidentAwaiting")}
                          </span>
                          <span className="rounded-full bg-juhuu-soft px-[.55rem] py-[.2rem] text-[.66rem] font-bold text-juhuu-deep">
                            {t("routes.dashboard.page.incidentLow")}
                          </span>
                          <span className="hidden whitespace-nowrap text-[.68rem] text-muted sm:inline">
                            {t(`routes.dashboard.page.incidents.${i}.date`)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Capabilities ─────────────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 grid items-end gap-6 md:grid-cols-[1fr_auto]">
              <div>
                <p className={kicker}>
                  {t("routes.dashboard.page.capKicker")}
                </p>
                <h2 className="mt-3.5 text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                  {t("routes.dashboard.page.capTitleLine1")}
                  <br />
                  {t("routes.dashboard.page.capTitleLine2")}
                </h2>
              </div>
              <p className="max-w-[340px] text-[.95rem] leading-[1.65] text-muted">
                {t("routes.dashboard.page.capLead")}
              </p>
            </div>

            <div className="overflow-hidden rounded-2xl border border-line bg-line">
              <div className="grid gap-px sm:grid-cols-2 lg:grid-cols-4">
                {CAP_ICONS.map((icon, i) => (
                  <div key={i} className="bg-white px-6 py-7">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="mb-4 h-[21px] w-[21px] text-juhuu"
                    >
                      {icon}
                    </svg>
                    <h3 className="text-[.98rem] font-bold tracking-[-0.01em] text-ink">
                      {t(`routes.dashboard.page.caps.${i}.title`)}
                    </h3>
                    <p className="mt-[.45rem] text-[.85rem] leading-[1.55] text-muted">
                      {t(`routes.dashboard.page.caps.${i}.description`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Booking / timeline ───────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div>
              <p className={kicker}>
                {t("routes.dashboard.page.bookingKicker")}
              </p>
              <h2 className={`mt-3 ${h2Section}`}>
                {t("routes.dashboard.page.bookingTitleLine1")}
                <br />
                {t("routes.dashboard.page.bookingTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.dashboard.page.bookingDescription")}
              </p>
              {bullets("routes.dashboard.page.bookingBullets")}
            </div>

            <div className={mockShell}>
              <MockBar url="dashboard.juhuu.app/bookings" />
              <div className="bg-white p-[1.1rem]">
                <div className="mb-3.5 flex items-center justify-between">
                  <div className="text-[1.1rem] font-extrabold text-ink">
                    {t("routes.dashboard.page.mockRentals")}
                  </div>
                  <span className="rounded-lg bg-ink px-[.7rem] py-[.35rem] text-[.74rem] font-bold text-white">
                    + {t("routes.dashboard.page.mockRentalBtn")}
                  </span>
                </div>
                <div className="mb-3.5 flex flex-wrap gap-2">
                  <span className="inline-flex items-center rounded-full border border-ink bg-ink px-[.7rem] py-[.3rem] text-[.74rem] font-semibold text-white">
                    {t("routes.dashboard.page.chipTimeline")}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-line-strong bg-white px-[.7rem] py-[.3rem] text-[.74rem] font-semibold text-muted">
                    {t("routes.dashboard.page.chipTable")}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-line-strong bg-white px-[.7rem] py-[.3rem] text-[.74rem] font-semibold text-muted">
                    {t("routes.dashboard.page.chipAllLocations")}
                  </span>
                </div>
                <div className="mb-3 flex flex-wrap gap-3 text-[.66rem] font-bold text-ink-2">
                  <span className="flex items-center gap-1.5">
                    <span className="h-[9px] w-[9px] rounded-[3px] bg-juhuu" />
                    {t("routes.dashboard.page.legendActive")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-[9px] w-[9px] rounded-[3px] bg-juhuu-soft-2" />
                    {t("routes.dashboard.page.legendUpcoming")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-[9px] w-[9px] rounded-[3px] bg-[#f5d57e]" />
                    {t("routes.dashboard.page.legendPending")}
                  </span>
                </div>

                <div className="overflow-hidden rounded-xl border border-line">
                  <div className="grid grid-cols-[84px_1fr] border-b border-line bg-bg-alt">
                    <div className="flex items-center border-r border-line px-[.7rem] py-[.6rem] text-[.62rem] font-bold text-light">
                      {t("routes.dashboard.page.timelineLocation")}
                    </div>
                    <div className="grid grid-cols-7 items-center text-center text-[.58rem] font-bold text-light">
                      {DAYS.map((d) => (
                        <span key={d}>{d}</span>
                      ))}
                    </div>
                  </div>
                  {TIMELINE_ROWS.map((r) => (
                    <div
                      key={r.loc}
                      className="grid grid-cols-[84px_1fr] border-b border-line last:border-b-0"
                    >
                      <div className="flex items-center border-r border-line px-[.7rem] py-[.6rem] text-[.72rem] font-bold text-ink">
                        {r.loc}
                      </div>
                      <div
                        className="relative h-[38px]"
                        style={{
                          background:
                            "repeating-linear-gradient(90deg,transparent 0 calc(14.28% - 1px),#ececf3 calc(14.28% - 1px) 14.28%)",
                        }}
                      >
                        {r.bars.map((b, bi) => (
                          <div
                            key={bi}
                            className="absolute top-[9px] flex h-5 items-center overflow-hidden whitespace-nowrap rounded-[5px] px-1.5 text-[.62rem] font-bold text-white"
                            style={{
                              left: `${b.l}%`,
                              width: `${b.w}%`,
                              background: b.c,
                            }}
                          >
                            {b.t || ""}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Device management ────────────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div className={`order-2 lg:order-1 ${mockShell}`}>
              <MockBar url="dashboard.juhuu.app/devices" />
              <div className="bg-white p-[1.1rem]">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-[1.1rem] font-extrabold text-ink">
                    {t("routes.dashboard.page.mockDevices")}
                  </div>
                  <span className="rounded-lg bg-ink px-[.8rem] py-[.35rem] text-[.74rem] font-bold text-white">
                    {t("routes.dashboard.page.mockCreateDevice")}
                  </span>
                </div>
                <p className="mb-3.5 text-[.78rem] text-muted">
                  {t("routes.dashboard.page.mockDevicesLead")}
                </p>
                <div className="overflow-hidden rounded-xl border border-line">
                  <div
                    className={`grid grid-cols-[1.3fr_.9fr_auto] gap-2.5 px-[1.1rem] py-[.6rem] ${trHead}`}
                  >
                    <span>{t("routes.dashboard.page.tableName")}</span>
                    <span>{t("routes.dashboard.page.tableStatus")}</span>
                    <span className="text-right">
                      {t("routes.dashboard.page.tableAction")}
                    </span>
                  </div>
                  {DEVICES.map(([name, online, hasAction]) => (
                    <div
                      key={name}
                      className="grid grid-cols-[1.3fr_.9fr_auto] items-center gap-2.5 border-b border-line px-[1.1rem] py-[.6rem] text-[.8rem] last:border-b-0"
                    >
                      <span className="font-bold text-ink">{name}</span>
                      <span className="text-ink-2">
                        <span
                          className={`mr-1.5 inline-block h-2 w-2 rounded-full ${
                            online ? "bg-juhuu" : "bg-light"
                          }`}
                        />
                        {online
                          ? t("routes.dashboard.page.statusOnline")
                          : t("routes.dashboard.page.statusOffline")}
                      </span>
                      <span className="text-right">
                        {hasAction ? (
                          <span className="whitespace-nowrap rounded-[7px] bg-ink px-[.7rem] py-[.3rem] text-[.7rem] font-bold text-white">
                            {t("routes.dashboard.page.openBox")}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <p className={kicker}>
                {t("routes.dashboard.page.devicesKicker")}
              </p>
              <h2 className={`mt-3 ${h2Section}`}>
                {t("routes.dashboard.page.devicesTitleLine1")}
                <br />
                {t("routes.dashboard.page.devicesTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.dashboard.page.devicesDescription")}
              </p>
              {bullets("routes.dashboard.page.devicesBullets")}
            </div>
          </div>
        </section>

        {/* ── Tariffs ──────────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div className={`order-2 lg:order-1 ${mockShell}`}>
              <MockBar url="dashboard.juhuu.app/tariffs" />
              <div className="bg-white p-[1.1rem]">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-[1.1rem] font-extrabold text-ink">
                    {t("routes.dashboard.page.mockTariffs")}
                  </div>
                  <span className="rounded-lg bg-ink px-[.8rem] py-[.35rem] text-[.74rem] font-bold text-white">
                    {t("routes.dashboard.page.mockCreate")}
                  </span>
                </div>
                <p className="mb-3.5 text-[.78rem] text-muted">
                  {t("routes.dashboard.page.mockTariffsLead")}
                </p>
                <div className="overflow-hidden rounded-xl border border-line">
                  <div
                    className={`grid grid-cols-[1.5fr_1fr_1fr] px-[1.1rem] py-[.7rem] ${trHead}`}
                  >
                    <span>{t("routes.dashboard.page.tableName")}</span>
                    <span>{t("routes.dashboard.page.tableDuration")}</span>
                    <span>{t("routes.dashboard.page.tableMaxAmount")}</span>
                  </div>
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[1.5fr_1fr_1fr] items-center border-b border-line px-[1.1rem] py-[.7rem] text-[.8rem] tabular-nums last:border-b-0 hover:bg-bg-alt"
                    >
                      <span className="font-bold text-ink">
                        {t(`routes.dashboard.page.tariffRows.${i}.name`)}
                      </span>
                      <span className="text-muted">
                        {t(`routes.dashboard.page.tariffRows.${i}.duration`)}
                      </span>
                      <span className="font-bold text-ink">
                        {t(`routes.dashboard.page.tariffRows.${i}.amount`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <p className={kicker}>
                {t("routes.dashboard.page.tariffsKicker")}
              </p>
              <h2 className={`mt-3 ${h2Section}`}>
                {t("routes.dashboard.page.tariffsTitleLine1")}
                <br />
                {t("routes.dashboard.page.tariffsTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.dashboard.page.tariffsDescription")}
              </p>
              {bullets("routes.dashboard.page.tariffsBullets")}
            </div>
          </div>
        </section>

        {/* ── Customers & benefit cards ────────────────────── */}
        <section className="bg-bg-alt px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div>
              <p className={kicker}>
                {t("routes.dashboard.page.customersKicker")}
              </p>
              <h2 className={`mt-3 ${h2Section}`}>
                {t("routes.dashboard.page.customersTitleLine1")}
                <br />
                {t("routes.dashboard.page.customersTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.dashboard.page.customersDescription")}
              </p>
              {bullets("routes.dashboard.page.customersBullets")}
            </div>

            <div className={mockShell}>
              <MockBar url="dashboard.juhuu.app/customers" />
              <div className="bg-white p-[1.1rem]">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-[1.1rem] font-extrabold text-ink">
                    {t("routes.dashboard.page.mockCustomers")}
                  </div>
                  <span className="rounded-lg bg-ink px-[.8rem] py-[.35rem] text-[.74rem] font-bold text-white">
                    + {t("routes.dashboard.page.mockUserBtn")}
                  </span>
                </div>
                <div className="mb-3.5 flex gap-2">
                  <span className="flex-1 rounded-full border border-line-strong px-[.8rem] py-[.4rem] text-[.74rem] text-light">
                    {t("routes.dashboard.page.mockSearchPlaceholder")}
                  </span>
                  <span className="rounded-full bg-juhuu-soft px-[.9rem] py-[.4rem] text-[.74rem] font-bold text-juhuu-deep">
                    {t("routes.dashboard.page.mockSearchBtn")}
                  </span>
                </div>
                <div className="overflow-hidden rounded-xl border border-line">
                  <div
                    className={`grid grid-cols-[1fr_1.3fr_auto] gap-2.5 px-[1.1rem] py-[.65rem] ${trHead}`}
                  >
                    <span>{t("routes.dashboard.page.tableName")}</span>
                    <span>{t("routes.dashboard.page.tableEmail")}</span>
                    <span className="text-right">
                      {t("routes.dashboard.page.tableVerified")}
                    </span>
                  </div>
                  {CUSTOMERS.map(([name, email, verified]) => (
                    <div
                      key={email}
                      className="grid grid-cols-[1fr_1.3fr_auto] items-center gap-2.5 border-b border-line px-[1.1rem] py-[.65rem] text-[.8rem] last:border-b-0 hover:bg-bg-alt"
                    >
                      <span className="font-bold text-ink">
                        {name}{" "}
                        <span className="rounded border border-line-strong px-1 py-px text-[.62rem] font-bold text-light">
                          DE
                        </span>
                      </span>
                      <span className="truncate text-muted">{email}</span>
                      <span className="text-right">
                        {verified ? (
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            className="inline h-[15px] w-[15px] text-juhuu"
                          >
                            <path d="m5 12 5 5L20 7" />
                          </svg>
                        ) : (
                          <span className="text-light">—</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Custom dashboards ────────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div>
              <p className={kicker}>
                {t("routes.dashboard.page.customKicker")}
              </p>
              <h2 className={`mt-3 ${h2Section}`}>
                {t("routes.dashboard.page.customTitleLine1")}
                <br />
                {t("routes.dashboard.page.customTitleLine2")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.dashboard.page.customDescription")}
              </p>
              {bullets("routes.dashboard.page.customBullets")}
            </div>

            <div className={mockShell}>
              <MockBar url="dashboard.juhuu.app/support" />
              <div className="bg-white p-[1.1rem]">
                <div className="mb-3.5 flex items-center gap-2">
                  <span className="text-[1.05rem] font-extrabold text-ink">
                    {t("routes.dashboard.page.mockSupportView")}
                  </span>
                  <span className="rounded-full bg-juhuu-soft px-[.55rem] py-[.2rem] text-[.62rem] font-bold text-juhuu-deep">
                    {t("routes.dashboard.page.mockCustomBadge")}
                  </span>
                </div>
                <div className="mb-4 flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center rounded-full border border-ink bg-ink px-[.7rem] py-[.28rem] text-[.72rem] font-semibold text-white">
                    {t("routes.dashboard.page.chipSupport")}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-line-strong bg-white px-[.7rem] py-[.28rem] text-[.72rem] font-semibold text-muted opacity-60">
                    {t("routes.dashboard.page.chipAccounting")}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-line-strong bg-white px-[.7rem] py-[.28rem] text-[.72rem] font-semibold text-muted opacity-60">
                    {t("routes.dashboard.page.chipOperations")}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2.5">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-line bg-white px-[.9rem] py-[.8rem]"
                    >
                      <div className="flex items-center gap-1.5 text-juhuu">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="h-[15px] w-[15px]"
                        >
                          {i === 0 && SIDE_ICONS.booking}
                          {i === 1 && SIDE_ICONS.support}
                          {i === 2 && (
                            <>
                              <rect x="4" y="4" width="16" height="16" rx="2" />
                              <rect x="9" y="9" width="6" height="6" />
                            </>
                          )}
                        </svg>
                        <span className="text-[.66rem] font-semibold uppercase tracking-[.06em] text-light">
                          {t(`routes.dashboard.page.customTiles.${i}.label`)}
                        </span>
                      </div>
                      <div className="mt-[.45rem] text-[1.25rem] font-extrabold leading-none tracking-[-0.02em] text-ink tabular-nums">
                        {t(`routes.dashboard.page.customTiles.${i}.value`)}
                      </div>
                      <div className="mt-[.35rem] text-[.7rem] font-bold text-light">
                        {t(`routes.dashboard.page.customTiles.${i}.sub`)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 overflow-hidden rounded-xl border border-line">
                  <div
                    className={`grid grid-cols-[1fr_auto] gap-2.5 px-[1.1rem] py-[.65rem] text-[.64rem] ${trHead}`}
                  >
                    <span>{t("routes.dashboard.page.recentRentals")}</span>
                    <span>{t("routes.dashboard.page.tableStatus")}</span>
                  </div>
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[1fr_auto] items-center gap-2.5 border-b border-line px-[1.1rem] py-[.65rem] text-[.8rem] last:border-b-0"
                    >
                      <span className="font-semibold text-ink-2">
                        {t(`routes.dashboard.page.recentRows.${i}.name`)}
                      </span>
                      <span className="rounded-full bg-juhuu-soft px-[.55rem] py-[.2rem] text-[.62rem] font-bold text-juhuu-deep">
                        {t(`routes.dashboard.page.recentRows.${i}.status`)}
                      </span>
                    </div>
                  ))}
                </div>

                <p className="mt-3 text-center text-[.68rem] text-muted">
                  {t("routes.dashboard.page.customNote")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Benefit strip ────────────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto grid max-w-7xl gap-8 text-center sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <div className="text-[clamp(2.1rem,3.4vw,3rem)] font-extrabold leading-none tracking-[-0.04em] text-juhuu">
                  {t(`routes.dashboard.page.stats.${i}.value`)}
                </div>
                <div className="mt-2 text-[.78rem] font-bold uppercase tracking-[.1em] text-light">
                  {t(`routes.dashboard.page.stats.${i}.label`)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Trusted by ───────────────────────────────────── */}
        <section className="px-4 md:px-8 py-14">
          <div className="mx-auto max-w-7xl">
            <p className="mb-6 text-center text-[.82rem] font-bold uppercase tracking-[.14em] text-muted">
              {t("routes.dashboard.page.trustedBy")}
            </p>
            <div className="w-full overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
              <div className="flex w-max animate-marquee items-center gap-[clamp(2rem,5vw,4rem)] hover:[animation-play-state:paused]">
                {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map(([slug, name], i) => (
                  <span key={i} className="inline-flex flex-shrink-0">
                    <img
                      src={require(`../newApp/assets/logos/${slug}.png`)}
                      alt={name}
                      loading="lazy"
                      className="h-10 w-auto max-w-[170px] object-contain opacity-55 transition-opacity hover:opacity-100"
                    />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────── */}
        <section className="bg-bg-tint px-4 md:px-8 py-20 text-center">
          <div className="mx-auto max-w-[640px]">
            <h2 className="text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
              {t("routes.dashboard.page.finalCtaTitle")}
            </h2>
            <p className="mx-auto my-6 max-w-[480px] text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
              {t("routes.dashboard.page.finalCtaDescription")}
            </p>
            <Link
              to="/meeting"
              className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
            >
              {t("routes.dashboard.page.finalCtaButton")}
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
